import { BaseWebComponentDataProvider } from '../../framework/BaseWebComponentDataProvider';
import { PhasrListItem } from '../../phasrRecommendations/phasrList/component/PhasrListItemWebComponent';
import { RuleTrigger, SEVERITY_COLORS } from '../types/types';
import { html } from 'lit';
import moment from 'moment';
import { ConsoleRequest, DataProviderType } from '../../utils/request';
import {
  PhasrRecommendationsEndpoint,
  PhasrRecommendationsMethod
} from '../../phasrRecommendations/phasrRecommendationsGrid/types';
import { GetBehavioralProfilesPayload, GetBehavioralProfilesResponse } from '../types/types';

export class PhasrSidePanelDataProvider extends BaseWebComponentDataProvider {
  public async getBehavioralProfiles(payload: GetBehavioralProfilesPayload): Promise<PhasrListItem[]> {
    const data = await ConsoleRequest.createRequest<Array<GetBehavioralProfilesResponse>>({
      dataProviderType: DataProviderType.CORE,
      endpoint: PhasrRecommendationsEndpoint.BEHAVIORAL_PROFILES,
      method: PhasrRecommendationsMethod.GET_PHASR_BEHAVIORAL_PROFILES_AGGREGATE,
      payload: payload,
    });

    // convert received data to PhasrListItem
    const items: PhasrListItem[] = [];
    for (const record of data) {
      items.push({
        id: record['behavioralProfileIdentity'],
        name: record['behavioralProfileIdentityName'],
        description: record['behavioralProfileResourceName'],
        icon: 'ds-icon-resource_user_role',
      });
    }

    return items;
  }

  public async getRuleTriggers(payload: any): Promise<any> {
    const data = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: PhasrRecommendationsEndpoint.RULE_TRIGGERS,
      method: PhasrRecommendationsMethod.GET_PHASR_RULE_TRIGGERS,
      payload: payload,
    }) as RuleTrigger[];
    const items: PhasrListItem[] = [];
    for (const record of data) {
      items.push({
        id: record.alertId,
        name: record.alertName,
        description: () => this.ruleTriggersDescriptionRenderer(record),
        icon: () => this.ruleTriggersIconRenderer(record.severity),
      });
    }
    return items;
  }
  private ruleTriggersDescriptionRenderer(record: RuleTrigger) {
    return html` <style>
      .rule-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: var(--spacer);
        margin: calc(var(--spacer) * 1) 0;
        .rule-row {
          display: flex;
        }
        .rule-row-item {
          font-size: 12px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 400;
          line-height: 18px;
          color: var(--main-titles-inputs-600);
          &.key {
            width: 40%;
          }
          &.value {
            width: 60%;
          }
          &.incidents {
            color: var(--blue-layout-400-primary);
          }
        }
      }
    </style>
    <div class="rule-wrapper">
      <div class="rule-row">
        <div class="rule-row-item key">Timestamp</div>
        <div class="rule-row-item value">${moment(record.alertCreatedDate).format('DD MMM YYYY, HH:mm:ss')}</div>
      </div>
      <div class="rule-row">
        <div class="rule-row-item key">Username</div>
        <div class="rule-row-item value">${record.userName}</div>
      </div>
      <div class="rule-row">
        <div class="rule-row-item key">Hostname</div>
        <div class="rule-row-item value">${record.hostName}</div>
      </div>
      <div class="rule-row">
        <div class="rule-row-item key">Incidents</div>
        <gz-link class="rule-row-item value incidents" type="quiet" color="primary" @click=${() => this.handleIncidentNumberClick(record.incidentId)}>#${record.incidentNumber}</gz-link>
      </div>
    </div>`;
  }

  private ruleTriggersIconRenderer(eventSeverity: number) {
    const color = eventSeverity === 3 ? SEVERITY_COLORS['highSeverity'] : eventSeverity === 2 ? SEVERITY_COLORS['mediumSeverity'] : SEVERITY_COLORS['lowSeverity'];
    return html`<gz-icon color="${color}" size="24">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" slot="custom-svg" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20.9829 6.6505V4.4298H20.468C20.3227 4.43706 20.1506 4.4407 19.9834 4.4407C17.1372 4.44594 14.4024 3.33454 12.3667 1.34528L12.0117 1L11.6543 1.34649C9.62446 3.30985 6.90995 4.40574 4.08596 4.40193C3.91392 4.40193 3.74229 4.39789 3.57106 4.38982H3.02952V6.61052C2.90837 12.0817 2.74239 19.5737 11.8118 22.7479L11.9802 22.8072L12.1292 22.7382C21.1901 19.6258 21.0714 12.1314 20.9829 6.6505ZM11.9741 21.7169C3.78429 18.7438 3.9224 12.0429 4.04234 6.6396V5.42081H4.14169C7.05261 5.42516 9.86433 4.36323 12.0456 2.43564L12.0323 2.44776C14.1983 4.40322 17.0144 5.48285 19.9326 5.47654H19.9628V6.68806C20.0719 12.0902 20.1906 18.8408 11.9729 21.7108L11.9741 21.7169ZM11.1261 13.8166H12.8343L13.2608 7.44283H10.702L11.1261 13.8166ZM11.1358 14.6647H12.8319V16.3608H11.1358V14.6647Z"
      />
    </svg>
  </gz-icon>`;
  }

  private handleIncidentNumberClick(incident: string): void {
    Application.lib.Navigation.openAppPageInNewTab('/incidents/' + incident);
  }
}
